import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import queryString from 'query-string';
import API from '../API/endpoints';
import { CHECKOUT } from '../utils/urls';

export const CHECKOUT_MODE = {
  SELECT: 'select',
  CHECKOUT: 'checkout',
};

export const flattenPlans = (apiPlans = []) => {
  const result = [];

  apiPlans.forEach((product) => {
    if (product.product_active === false) {
      return;
    }

    (product.plans || []).forEach((price) => {
      if (price.active === false) {
        return;
      }

      result.push({
        productId: product.product_id,
        productName: product.product_name,
        productDescription: product.product_description,
        priceId: price.price_id,
        nickname: price.nickname,
        amount: Number(price.amount),
        currency: (price.currency || 'USD').toUpperCase(),
        interval: price.interval,
        intervalCount: price.interval_count || 1,
        type: price.type,
      });
    });
  });

  return result.sort((a, b) => a.amount - b.amount);
};

export const findPlanById = (plans, planId) => {
  if (!planId) {
    return null;
  }

  return (
    plans.find((plan) => plan.priceId === planId) ||
    plans.find((plan) => plan.productId === planId) ||
    null
  );
};

const buildCheckoutPath = (params) => {
  const search = queryString.stringify(params);
  return search ? `${CHECKOUT}?${search}` : CHECKOUT;
};

function useCheckoutPlans() {
  const history = useHistory();
  const location = useLocation();
  const accessToken = useSelector((state) => state.session && state.session.accessToken);

  const query = queryString.parse(location.search);
  const planId = query.plan_id || null;
  const paymentStatus = query.status || null;

  const [rawPlans, setRawPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  const plans = useMemo(() => flattenPlans(rawPlans), [rawPlans]);
  const selectedPlan = useMemo(
    () => findPlanById(plans, planId),
    [plans, planId]
  );
  const mode = planId ? CHECKOUT_MODE.CHECKOUT : CHECKOUT_MODE.SELECT;

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await API.getPlans();
      setRawPlans((response.data && response.data.plans) || []);
    } catch (err) {
      setRawPlans([]);
      setError('No se pudieron cargar los planes. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const selectPlan = useCallback(
    (nextPlanId) => {
      history.push(buildCheckoutPath({ plan_id: nextPlanId }));
    },
    [history]
  );

  const clearPlan = useCallback(() => {
    history.push(CHECKOUT);
  }, [history]);

  const startCheckoutSession = useCallback(async () => {
    if (!selectedPlan) {
      setCheckoutError('Selecciona un plan para continuar');
      return;
    }

    if (!accessToken) {
      setCheckoutError('Inicia sesión para continuar con el pago');
      return;
    }

    setCheckoutLoading(true);
    setCheckoutError(null);

    const origin = window.location.origin;
    const body = {
      items: [{ price_id: selectedPlan.priceId, quantity: 1 }],
      mode: 'subscription',
      success_url: `${origin}${buildCheckoutPath({
        plan_id: selectedPlan.priceId,
        status: 'success',
      })}`,
      cancel_url: `${origin}${buildCheckoutPath({
        plan_id: selectedPlan.priceId,
        status: 'cancel',
      })}`,
      allow_promotion_codes: true,
    };

    try {
      const response = await API.createCheckoutSession(body, accessToken);
      const sessionUrl = response.data && response.data.session_url;

      if (!sessionUrl) {
        setCheckoutError('No se pudo iniciar la sesión de pago');
        return;
      }

      window.location.assign(sessionUrl);
    } catch (err) {
      const apiError =
        err.response && err.response.data && err.response.data.error;
      setCheckoutError(apiError || 'Error al crear la sesión de checkout');
    } finally {
      setCheckoutLoading(false);
    }
  }, [accessToken, selectedPlan]);

  return {
    mode,
    planId,
    plans,
    selectedPlan,
    loading,
    error,
    selectPlan,
    clearPlan,
    startCheckoutSession,
    checkoutLoading,
    checkoutError,
    paymentStatus,
    refetchPlans: fetchPlans,
  };
}

export default useCheckoutPlans;
